import { BP_TABLET, BP_LAPTOP, BP_DESKTOP } from '@rsos/constants/breakpoints';
import type {
  CallMetadata,
  WindowData,
  FeedbackFormData,
  CallQueueData,
  TranscriptionData,
  LocationData,
  ScreenSize,
  TranscriptToken,
} from './types';

export const getMostRecentLocation = (
  callQueueData: CallQueueData | null,
): LocationData | null => {
  if (!callQueueData?.core) return null;

  const { locations = [], secondaryLocations = [] } = callQueueData.core;
  const lastLocationIdx = locations.length - 1;
  const lastSecondaryIdx = secondaryLocations.length - 1;

  if (locations.length && secondaryLocations.length) {
    const lastLocation = locations[lastLocationIdx];
    const lastSecondary = secondaryLocations[lastSecondaryIdx];

    return (lastLocation.call_start_time ?? 0) >
      (lastSecondary.call_start_time ?? 0)
      ? lastLocation
      : lastSecondary;
  }

  return (
    locations[lastLocationIdx] || secondaryLocations[lastSecondaryIdx] || null
  );
};

export const getCallMetadata = (
  callQueueData: CallQueueData | null,
  transcriptionData: TranscriptionData | null,
  callerId: string,
): CallMetadata => {
  const mostRecentLocation = getMostRecentLocation(callQueueData);
  const latestSummary = transcriptionData?.summaries?.[0];
  const fallbackKeywords = transcriptionData?.keywords || [];
  const keywords = latestSummary?.keywords?.length
    ? latestSummary.keywords
    : fallbackKeywords;

  const tokens = transcriptionData?.payload?.tokens || [];

  return {
    digiCallId: transcriptionData?.payload?.call_information?.digi_call_id,
    transcriptionTokens: tokens,
    summaryText: latestSummary?.summary || '',
    keywords,
    detectedLanguages: transcriptionData?.payload?.languagesDetected || [],
    callerPhoneNumber: mostRecentLocation?.caller_id || callerId,
    callStartTime: mostRecentLocation?.call_start_time,
    audioUrl: transcriptionData?.payload?.audio_data?.audio_url,
    uncertaintyRadius: mostRecentLocation?.uncertainty_radius,
    locationSource:
      mostRecentLocation?.source ||
      (mostRecentLocation?.application === 'sms' ? 'sms' : 'call'),
    callDuration: mostRecentLocation?.call_start_time
      ? Math.floor((Date.now() - mostRecentLocation.call_start_time) / 1000)
      : null,
    keywordCount: keywords.length,
  };
};

export const getWindowData = (): WindowData => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const aspectRatio = parseFloat((width / height).toFixed(2));

  const getScreenSize = (width: number): ScreenSize => {
    if (width < BP_TABLET) return 'mobile';
    if (width < BP_LAPTOP) return 'tablet';
    if (width < BP_DESKTOP) return 'laptop';
    return 'desktop';
  };

  return {
    windowWidth: width,
    windowHeight: height,
    orientation: width > height ? 'landscape' : 'portrait',
    devicePixelRatio: window.devicePixelRatio || 1,
    screenResolution: `${screen.width}x${screen.height}`,
    viewportArea: width * height,
    aspectRatio,
    screenSize: getScreenSize(width),
  };
};

export const validateFormData = (formData: FeedbackFormData): boolean => {
  return Boolean(formData.category.trim());
};

export interface SubmitFeedbackRequest {
  category: string;
  freeform_text?: string;
  caller_id: string;
  digi_call_id?: string;
  authority_id?: number;
  authority_display_name?: string;
  authority_account_id?: string;
  transcription_tokens?: TranscriptToken[];
  audio_url?: string | null;
  call_start_time?: string;
  call_duration?: number;
  summary_text?: string;
  keywords?: string[];
  detected_languages?: string[];
  uncertainty_radius?: number;
  location_source?: string;
  submission_source: string;
  window_width?: number;
  window_height?: number;
  orientation?: string;
  device_pixel_ratio?: number;
  screen_resolution?: string;
}

export interface AuthorityData {
  authorityId?: number;
  authorityDisplayName?: string;
  authorityAccountId?: string;
}

export interface SubmitFeedbackArgs {
  formData: FeedbackFormData;
  callMetadata: CallMetadata;
  windowData: WindowData;
  callerId: string;
  authorityData?: AuthorityData;
}

export const transformFeedbackToRequest = ({
  formData,
  callMetadata,
  windowData,
  callerId,
  authorityData,
}: SubmitFeedbackArgs): SubmitFeedbackRequest => {
  return {
    category: formData.category,
    freeform_text: formData.freeformText || undefined,
    caller_id: callerId,
    digi_call_id: callMetadata.digiCallId ?? '', // Explicitly include even if undefined if feedback added without a call
    authority_id: authorityData?.authorityId,
    authority_display_name: authorityData?.authorityDisplayName,
    authority_account_id: authorityData?.authorityAccountId,
    submission_source: 'emergency_data_app',
    window_width: windowData.windowWidth,
    window_height: windowData.windowHeight,
    orientation: windowData.orientation,
    device_pixel_ratio: windowData.devicePixelRatio,
    screen_resolution: windowData.screenResolution,
    transcription_tokens: callMetadata.transcriptionTokens,
    audio_url: callMetadata.audioUrl || undefined, // Explicitly include even if undefined
    call_start_time: callMetadata.callStartTime
      ? new Date(callMetadata.callStartTime).toISOString()
      : undefined,
    call_duration: callMetadata.callDuration ?? undefined,
    summary_text: callMetadata.summaryText,
    keywords: callMetadata.keywords,
    detected_languages: callMetadata.detectedLanguages,
    uncertainty_radius: callMetadata.uncertaintyRadius,
    location_source: callMetadata.locationSource,
  };
};
